import { ReactNode } from 'react'
import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { PropertyView, PropertySection, PropertyField } from './PropertyView'
import { fmtCurrency } from '../utils/propertyFormat'
import { useLocalizedLabel, useEnumOptions } from '../utils/getLocalizedLabel'

export type ChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'

export type FieldKind =
  | { kind: 'text' }
  | { kind: 'multiline'; rows?: number }
  | { kind: 'number'; min?: number; max?: number; step?: number; unit?: string }
  | { kind: 'currency'; currencyKey?: string }
  | { kind: 'date' }
  | { kind: 'datetime' }
  | { kind: 'boolean' }
  | { kind: 'rating' }
  | { kind: 'id' }
  | { kind: 'enum'; enumName: string }
  | {
      kind: 'chip'
      enumName: string
      color?: (v: string) => ChipColor
    }
  | { kind: 'phone' }
  | { kind: 'email' }
  | { kind: 'url' }
  | { kind: 'custom'; render: (value: unknown) => ReactNode }

export interface FieldSchema {
  key: string
  label: string
  kind: FieldKind
  /** Always read-only even in edit mode (system fields like id, createdAt). */
  readOnly?: boolean
  /** Span the full row in the grid. */
  full?: boolean
  /** Help text shown under the field in edit mode. */
  helper?: string
}

export interface FieldGroup {
  title: string
  fields: FieldSchema[]
  columns?: 1 | 2 | 3
  /** Show the group only if this predicate returns true. */
  visible?: (data: Record<string, unknown>) => boolean
}

export interface EntityPropertiesProps {
  data: Record<string, unknown>
  groups: FieldGroup[]
  editMode?: boolean
  onChange?: (next: Record<string, unknown>) => void
}

function getValue(data: Record<string, unknown>, key: string): unknown {
  if (!key.includes('.')) return data[key]
  return key.split('.').reduce<unknown>(
    (acc, part) => (acc != null && typeof acc === 'object' ? (acc as Record<string, unknown>)[part] : undefined),
    data,
  )
}

function setValue(
  data: Record<string, unknown>,
  key: string,
  value: unknown,
): Record<string, unknown> {
  if (!key.includes('.')) return { ...data, [key]: value }
  const [head, ...rest] = key.split('.')
  const child = (data[head] && typeof data[head] === 'object' ? data[head] : {}) as Record<string, unknown>
  return { ...data, [head]: setValue(child, rest.join('.'), value) }
}

function toDateInputValue(value: unknown, withTime: boolean): string {
  if (value == null || value === '') return ''
  const d = new Date(value as string)
  if (isNaN(d.getTime())) return ''
  const iso = d.toISOString()
  return withTime ? iso.slice(0, 16) : iso.slice(0, 10)
}

function ViewField({ field, data }: { field: FieldSchema; data: Record<string, unknown> }) {
  const v = getValue(data, field.key)
  const k = field.kind
  const getLocalizedLabel = useLocalizedLabel()

  switch (k.kind) {
    case 'id':
      return <PropertyField label={field.label} value={v as string} mono copyable full={field.full} />
    case 'date':
      return (
        <PropertyField
          label={field.label}
          value={v as string}
          type="date"
          full={field.full}
        />
      )
    case 'datetime':
      return (
        <PropertyField
          label={field.label}
          value={v as string}
          type="datetime"
          full={field.full}
        />
      )
    case 'boolean':
      return (
        <PropertyField label={field.label} full={field.full}>
          <Chip
            label={v ? 'Yes' : 'No'}
            size="small"
            color={v ? 'success' : 'default'}
            variant={v ? 'filled' : 'outlined'}
          />
        </PropertyField>
      )
    case 'rating': {
      const num = Number(v)
      if (!isFinite(num)) {
        return <PropertyField label={field.label} value={null} full={field.full} />
      }
      return (
        <PropertyField label={field.label} full={field.full}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Rating value={num} readOnly precision={0.5} size="small" />
            <Typography variant="caption" color="text.secondary">
              {num.toFixed(1)}
            </Typography>
          </Stack>
        </PropertyField>
      )
    }
    case 'enum': {
      const label = v ? getLocalizedLabel(k.enumName, String(v)) : null
      return <PropertyField label={field.label} value={label} full={field.full} />
    }
    case 'chip': {
      if (v == null || v === '') {
        return <PropertyField label={field.label} value={null} full={field.full} />
      }
      const labelText = getLocalizedLabel(k.enumName, String(v))
      const color = k.color?.(String(v)) || 'default'
      return (
        <PropertyField label={field.label} full={field.full}>
          <Chip label={labelText} size="small" color={color} />
        </PropertyField>
      )
    }
    case 'currency': {
      const code = (k.currencyKey ? (data[k.currencyKey] as string) : undefined) || 'USD'
      return (
        <PropertyField
          label={field.label}
          value={typeof v === 'number' ? fmtCurrency(v, code) : null}
          full={field.full}
        />
      )
    }
    case 'number': {
      const display =
        v == null || v === ''
          ? null
          : `${Number(v).toLocaleString()}${k.unit ? ` ${k.unit}` : ''}`
      return <PropertyField label={field.label} value={display} full={field.full} />
    }
    case 'multiline':
      return (
        <PropertyField label={field.label} full={field.full}>
          {v ? (
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {String(v)}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          )}
        </PropertyField>
      )
    case 'custom':
      return (
        <PropertyField label={field.label} full={field.full}>
          {k.render(v)}
        </PropertyField>
      )
    default:
      return <PropertyField label={field.label} value={v as string} full={field.full} />
  }
}

function EditField({
  field,
  data,
  onChange,
}: {
  field: FieldSchema
  data: Record<string, unknown>
  onChange: (key: string, next: unknown) => void
}) {
  const v = getValue(data, field.key)
  const k = field.kind
  const disabled = !!field.readOnly
  const getEnumOptions = useEnumOptions()

  const wrap = (node: ReactNode) => (
    <Box sx={{ gridColumn: field.full ? '1 / -1' : 'auto', minWidth: 0 }}>{node}</Box>
  )

  switch (k.kind) {
    case 'id':
      return wrap(
        <TextField
          fullWidth
          size="small"
          label={field.label}
          value={(v as string) ?? ''}
          disabled
          InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
        />,
      )
    case 'text':
      return wrap(
        <TextField
          fullWidth
          size="small"
          label={field.label}
          value={(v as string) ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(field.key, e.target.value === '' ? null : e.target.value)}
          helperText={field.helper}
        />,
      )
    case 'multiline':
      return wrap(
        <TextField
          fullWidth
          size="small"
          label={field.label}
          value={(v as string) ?? ''}
          disabled={disabled}
          multiline
          minRows={k.rows ?? 3}
          onChange={(e) => onChange(field.key, e.target.value === '' ? null : e.target.value)}
          helperText={field.helper}
        />,
      )
    case 'number':
      return wrap(
        <TextField
          fullWidth
          size="small"
          type="number"
          label={field.label}
          value={v == null ? '' : (v as number)}
          disabled={disabled}
          inputProps={{ min: k.min, max: k.max, step: k.step ?? 1 }}
          onChange={(e) => {
            const t = e.target.value
            onChange(field.key, t === '' ? null : Number(t))
          }}
          helperText={field.helper ?? k.unit}
        />,
      )
    case 'currency': {
      const code = (k.currencyKey ? (data[k.currencyKey] as string) : undefined) || ''
      return wrap(
        <TextField
          fullWidth
          size="small"
          type="number"
          label={field.label}
          value={v == null ? '' : (v as number)}
          disabled={disabled}
          inputProps={{ min: 0, step: 0.01 }}
          InputProps={{ endAdornment: code ? <Box sx={{ ml: 1, color: 'text.secondary' }}>{code}</Box> : undefined }}
          onChange={(e) => {
            const t = e.target.value
            onChange(field.key, t === '' ? null : Number(t))
          }}
          helperText={field.helper}
        />,
      )
    }
    case 'date':
      return wrap(
        <TextField
          fullWidth
          size="small"
          type="date"
          label={field.label}
          value={toDateInputValue(v, false)}
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => onChange(field.key, e.target.value === '' ? null : e.target.value)}
          helperText={field.helper}
        />,
      )
    case 'datetime':
      return wrap(
        <TextField
          fullWidth
          size="small"
          type="datetime-local"
          label={field.label}
          value={toDateInputValue(v, true)}
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
          onChange={(e) =>
            onChange(field.key, e.target.value === '' ? null : new Date(e.target.value).toISOString())
          }
          helperText={field.helper}
        />,
      )
    case 'boolean':
      return wrap(
        <FormControlLabel
          control={
            <Switch
              checked={!!v}
              disabled={disabled}
              onChange={(e) => onChange(field.key, e.target.checked)}
            />
          }
          label={field.label}
        />,
      )
    case 'rating':
      return wrap(
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {field.label}
          </Typography>
          <Rating
            value={Number(v) || 0}
            disabled={disabled}
            precision={0.5}
            onChange={(_, next) => onChange(field.key, next)}
          />
        </Box>,
      )
    case 'enum':
    case 'chip': {
      const options = getEnumOptions(k.enumName)
      const currentValue = (v as string) ?? ''
      const hasCurrent = !currentValue || options.some((o) => o.code === currentValue)
      return wrap(
        <FormControl fullWidth size="small" disabled={disabled}>
          <InputLabel id={`enum-${field.key}`}>{field.label}</InputLabel>
          <Select
            labelId={`enum-${field.key}`}
            label={field.label}
            value={currentValue}
            onChange={(e) => onChange(field.key, e.target.value === '' ? null : e.target.value)}
          >
            <MenuItem value="">
              <em>—</em>
            </MenuItem>
            {!hasCurrent && (
              <MenuItem value={currentValue} disabled>
                {currentValue} (unknown)
              </MenuItem>
            )}
            {options.map((opt) => (
              <MenuItem key={opt.code} value={opt.code}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>,
      )
    }
    case 'phone':
      return wrap(
        <TextField
          fullWidth
          size="small"
          type="tel"
          label={field.label}
          value={(v as string) ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(field.key, e.target.value === '' ? null : e.target.value)}
          helperText={field.helper}
        />,
      )
    case 'email':
      return wrap(
        <TextField
          fullWidth
          size="small"
          type="email"
          label={field.label}
          value={(v as string) ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(field.key, e.target.value === '' ? null : e.target.value)}
          helperText={field.helper}
        />,
      )
    case 'url':
      return wrap(
        <TextField
          fullWidth
          size="small"
          type="url"
          label={field.label}
          value={(v as string) ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(field.key, e.target.value === '' ? null : e.target.value)}
          helperText={field.helper}
        />,
      )
    case 'custom':
      return wrap(<>{k.render(v)}</>)
    default:
      return null
  }
}

function GroupTitle({ title }: { title: string }) {
  return (
    <Typography
      variant="overline"
      sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.8, lineHeight: 1.6 }}
    >
      {title}
    </Typography>
  )
}

function EditGroup({
  group,
  data,
  onChange,
}: {
  group: FieldGroup
  data: Record<string, unknown>
  onChange: (key: string, next: unknown) => void
}) {
  return (
    <Box>
      <GroupTitle title={group.title} />
      <Box
        sx={{
          mt: 1,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: `repeat(${group.columns ?? 2}, minmax(0, 1fr))`,
          },
          columnGap: 2,
          rowGap: 2,
        }}
      >
        {group.fields.map((f) => (
          <EditField key={f.key} field={f} data={data} onChange={onChange} />
        ))}
      </Box>
    </Box>
  )
}

export function EntityProperties({ data, groups, editMode, onChange }: EntityPropertiesProps) {
  const handleChange = (key: string, next: unknown) => {
    if (!onChange) return
    onChange(setValue(data, key, next))
  }

  const visibleGroups = groups.filter((g) => !g.visible || g.visible(data))

  if (editMode) {
    return (
      <Stack spacing={3} sx={{ width: '100%' }}>
        {visibleGroups.map((g) => (
          <EditGroup key={g.title} group={g} data={data} onChange={handleChange} />
        ))}
      </Stack>
    )
  }

  return (
    <PropertyView>
      {visibleGroups.map((g) => (
        <PropertySection key={g.title} title={g.title} columns={g.columns}>
          {g.fields.map((f) => (
            <ViewField key={f.key} field={f} data={data} />
          ))}
        </PropertySection>
      ))}
    </PropertyView>
  )
}

